import {useUpmPlant} from '@hconnect/upm'
import {
  Button,
  Modal,
  ModalTitle,
  ModalContent,
  ControlledInput,
  Label,
  Loading,
  clx
} from '@hm/fundamental'
import {mdiCheck, mdiClose, mdiDeleteOutline} from '@mdi/js'
import moment from 'moment'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {materialAtMeter} from './helpers'

import {useRefractoryQuery} from '@refra/modules/common/hooks/queries'
import {useKilns, useRefraModal} from '@refra/modules/common/providers'
import {Shutdown} from '@refra/modules/common/types'
import {useEditKilnReplacement} from '@refra/modules/refra-planning/hooks/mutations'
import {useKilnMaterialInstallationsQuery} from '@refra/modules/refra-planning/hooks/queries/useKilnMaterialInstallationsQuery'
import {EditHotSpotDTO, HotSpot} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface HotspotModalProps {
  shutdown: Shutdown
  hotSpots: HotSpot[]
  hotSpot?: HotSpot
}

export const HotspotModal = ({shutdown, hotSpots, hotSpot}: HotspotModalProps) => {
  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const {selectedKiln} = useKilns()
  const {t} = useTranslation()
  const {closeModal} = useRefraModal()
  const [meter, setMeter] = useState<number>(hotSpot?.meter ?? 0)

  const {mutate: updateHotspots} = useEditKilnReplacement()

  const {data: materialInstallations, isLoading: isLoadingInstallation} =
    useKilnMaterialInstallationsQuery({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      date: moment(shutdown.interventionDate).subtract(1, 'days').toISOString()
    })

  const usedMaterial = useMemo(
    () => materialAtMeter(materialInstallations, meter),
    [meter, materialInstallations]
  )

  const {data: refractory} = useRefractoryQuery({
    refractoryId: usedMaterial?.refractoryId ?? 0,
    enabled: usedMaterial !== undefined
  })

  const onClose = () => {
    setMeter(0)
    closeModal()
  }

  const onSubmit = () => {
    const existingHotSpot = hotSpots.findIndex((hs) => hs.id === hotSpot?.id)
    const newHotSpots: EditHotSpotDTO[] = [...hotSpots]
    if (existingHotSpot !== -1) {
      newHotSpots[existingHotSpot].meter = meter
    } else {
      newHotSpots.push({meter})
    }
    updateHotspots({
      kilnUpmId: selectedKiln.upmId,
      plantUpmId: upmPlant.upmId,
      shutdownId: shutdown.id,
      kilnReplacementDTO: {
        hotSpots: newHotSpots
      }
    })
    closeModal()
  }

  const onDelete = () => {
    const newHotSpots = hotSpots.filter((hs) => hs.id !== hotSpot?.id)
    updateHotspots({
      kilnUpmId: selectedKiln.upmId,
      plantUpmId: upmPlant.upmId,
      shutdownId: shutdown.id,
      kilnReplacementDTO: {
        hotSpots: newHotSpots
      }
    })
    closeModal()
  }

  const refractoryLabel = refractory?.name ?? t('refraPlanning.hotspots.noMaterialDetected')

  return (
    <Modal open onClose={() => onClose()} data-cy="refra_add_hotspot_modal">
      <ModalTitle data-cy="refra_add_hotspot_modal_title">
        {t('refraPlanning.hotspots.hotspotDetails')}
      </ModalTitle>
      <ModalContent>
        <div className="flex flex-col gap-4">
          <ControlledInput
            label={t('refraPlanning.hotspots.hotspotMeter')}
            value={meter?.toString()}
            className="w-full"
            data-cy="refra_add_hotspot_modal_meter"
            onChange={(e) => setMeter(parseFloat(e.target.value))}
            min={0}
            step={0.1}
            type="number"
          />
          <Label
            title={t('refraPlanning.kilnShutdown.material')}
            {...{children: isLoadingInstallation ? <Loading size="small" /> : refractoryLabel}}
          />
        </div>
      </ModalContent>
      <div className={clx('flex flex-row mt-6', hotSpot?.id ? 'justify-between' : 'justify-end')}>
        {hotSpot?.id && (
          <Button
            onClick={() => onDelete()}
            icon={mdiDeleteOutline}
            variant="negative"
            data-cy="refra_add_hotspot_modal_delete_button"
          >
            {t('common.delete')}
          </Button>
        )}
        <div className="flex gap-2">
          <Button
            onClick={() => onClose()}
            icon={mdiClose}
            variant="secondary"
            data-cy="refra_add_hotspot_modal_close_button"
          >
            {t('common.cancel')}
          </Button>
          <Button data-cy="refra_add_hotspot_modal_save_button" icon={mdiCheck} onClick={onSubmit}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
